import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ProfileService } from '../../services/profile.service';
import { PermissionService } from '../../services/permission.service';
import { ToastrService } from 'ngx-toastr';
import { Router, ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ProfileInput, ProfileUpdateInput, ProfileOutput } from '../../services/models/profile.model';
import { PermissionOutput } from '../../services/models/permission.model';
import { PermissionTreeComponent } from '../../components/permission-tree/permission-tree.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-profile-create',
  standalone: true,
  imports: [FormsModule, CommonModule, PermissionTreeComponent, TranslocoModule],
  templateUrl: './profile-create.component.html',
  styleUrl: './profile-create.component.css'
})
export class ProfileCreateComponent implements OnInit {
  name: string = '';
  description: string = '';
  selectedPermissionKeys: string[] = [];
  isActive: boolean = true;
  
  permissions: PermissionOutput[] = [];
  formSubmitted: boolean = false;
  profileId: string | null = null;
  isEditMode: boolean = false;

  constructor(
    private profileService: ProfileService,
    private permissionService: PermissionService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private route: ActivatedRoute,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.loadPermissions();
    
    this.route.paramMap.subscribe(params => {
      this.profileId = params.get('profileId');
      this.isEditMode = !!this.profileId;

      if (this.isEditMode && this.profileId) {
        this.loadProfile(this.profileId);
      }
    });
  }

  loadPermissions(): void {
    this.spinner.show();
    this.permissionService.getTree()
      .subscribe({
        next: (permissions) => {
          this.permissions = permissions;
          this.spinner.hide();
        },
        error: (error) => {
          console.error('Erro ao carregar árvore de permissões:', error);
          this.toastr.error(this.transloco.translate('users.toast.loadPermissionsError'));
          this.spinner.hide();
        }
      });
  }

  loadProfile(profileId: string): void {
    this.spinner.show();
    this.profileService.getById(profileId).subscribe({
      next: (profile) => {
        this.name = profile.name;
        this.description = profile.description || '';
        this.selectedPermissionKeys = profile.permissionKeys || [];
        this.isActive = profile.isActive;
        this.spinner.hide();
      },
      error: (error) => {
        console.error('Erro ao carregar perfil:', error);
        this.toastr.error(this.transloco.translate('users.toast.loadProfileError'));
        this.spinner.hide();
        this.router.navigate(['/users'], { queryParams: { tab: 'perfis' } });
      }
    });
  }

  save() {
    this.formSubmitted = true;

    // Validações para criação
    if (!this.isEditMode) {
      if (!this.name || this.name.trim().length < 3) {
        return;
      }
      if (!this.selectedPermissionKeys || this.selectedPermissionKeys.length === 0) {
        return;
      }
    } else {
      // Validações para edição
      if (this.name && this.name.trim().length < 3) {
        return;
      }
      if (this.selectedPermissionKeys && this.selectedPermissionKeys.length === 0) {
        return;
      }
    }

    this.spinner.show();

    if (this.isEditMode && this.profileId) {
      this.updateProfile();
    } else {
      this.createProfile();
    }
  }

  onPermissionKeysChange(keys: string[]): void {
    this.selectedPermissionKeys = keys;
  }

  createProfile(): void {
    const profileInput: ProfileInput = {
      name: this.name.trim(),
      description: this.description.trim() || undefined,
      permissionKeys: this.selectedPermissionKeys,
      isActive: this.isActive
    };

    this.profileService.create(profileInput)
      .subscribe({
        next: () => {
          this.toastr.success(this.transloco.translate('users.toast.profileCreated'));
          this.spinner.hide();
          this.router.navigate(['/users'], { queryParams: { tab: 'perfis' } });
        },
        error: (error) => {
          console.error('Erro ao criar perfil:', error);
          this.handleError(error, 'criar');
          this.spinner.hide();
        }
      });
  }

  updateProfile(): void {
    const profileUpdate: ProfileUpdateInput = {};

    if (this.name) {
      profileUpdate.name = this.name.trim();
    }
    if (this.description !== undefined) {
      profileUpdate.description = this.description.trim() || undefined;
    }
    if (this.selectedPermissionKeys && this.selectedPermissionKeys.length > 0) {
      profileUpdate.permissionKeys = this.selectedPermissionKeys;
    }
    if (this.isActive !== undefined) {
      profileUpdate.isActive = this.isActive;
    }

    this.profileService.update(this.profileId!, profileUpdate)
      .subscribe({
        next: () => {
          this.toastr.success(this.transloco.translate('users.toast.profileUpdated'));
          this.spinner.hide();
          this.router.navigate(['/users'], { queryParams: { tab: 'perfis' } });
        },
        error: (error) => {
          console.error('Erro ao atualizar perfil:', error);
          this.handleError(error, 'atualizar');
          this.spinner.hide();
        }
      });
  }

  handleError(error: any, action: string): void {
    if (error.status === 400) {
      const errorMessage = error.error?.message || this.transloco.translate('users.toast.profileDataError');
      this.toastr.error(errorMessage);
    } else if (error.status === 404) {
      this.toastr.error(this.transloco.translate('users.toast.profileNotFound'));
    } else {
      this.toastr.error(this.transloco.translate('users.toast.profileGenericError'));
    }
  }

  cancelar() {
    this.router.navigate(['/users'], { queryParams: { tab: 'perfis' } });
  }
}
